import { useCallback, useEffect, useState } from 'react';

import { Add, Close, Delete, Save } from '@mui/icons-material';
import {
	LinearProgress,
	TextField,
	Button,
	Typography,
	Box,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Switch,
	FormControlLabel,
} from '@mui/material';

import useInAppAvailabilities from 'hooks/useInAppAvailabilities';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers';
import useInAppAvailabilitiesActions from 'hooks/useInAppAvailabilitiesActions';
import { InAppSaleType, inAppSaleTypes, ReservationType, reservationTypes } from 'lib/models/order';
import useVehicles from 'hooks/useVehicles';

type Props = {
	productId?: string;
};

export default function Availability({ productId }: Props) {
	const {
		availability,
		loading: loadingAvailability,
		refetch: refetchAvailability,
	} = useInAppAvailabilities(productId);
	const { loading: loadingAvailabilitiesActions, updateAvailability, deleteAvailability } =
		useInAppAvailabilitiesActions();

	const [availabilityDailyLimit, setAvailabilityDailyLimit] = useState(0);
	const [availabilityMonthlyLimit, setAvailabilityMonthlyLimit] = useState<number>(0);
	const [availabilityExceptions, setAvailabilityExceptions] = useState<InAppAvailabilityException[]>([]);
	const [newException, setNewException] = useState<InAppAvailabilityException | null>(null);
	const [accessType, setAccessType] = useState<string | undefined>();
	const [orderTypeEnabled, setOrderTypeEnabled] = useState<boolean>(false);

	const { vehicles } = useVehicles();

	const loadingAvailabilities = loadingAvailability || loadingAvailabilitiesActions;

	const [type, setType] = useState<InAppSaleType>(InAppSaleType.order);
	const [reservationType, setReservationType] = useState<ReservationType>(ReservationType.resort);

	useEffect(() => {
		if (availability) {
			setType(availability.type);

			if (availability.reservationType) {
				setReservationType(availability.reservationType);
			}
			if (availability.accessType) {
				setAccessType(availability.accessType);
			}
			// For 'order' type: if availability exists, it means it's enabled
			setOrderTypeEnabled(true);
		} else {
			// No availability = disabled for 'order' type
			setOrderTypeEnabled(false);
		}
	}, [availability]);

	const saveAvailability = useCallback(async () => {
		if (!!productId && Array.isArray(availabilityExceptions)) {
			// For 'order' type: handle enable/disable
			if (type === InAppSaleType.order) {
				if (!orderTypeEnabled && availability?._id) {
					// Switch is OFF and availability exists -> DELETE it
					await deleteAvailability(availability._id);
				} else if (orderTypeEnabled) {
					// Switch is ON -> CREATE/UPDATE with dailyLimit=0, monthlyLimit=0
					let data: InAppAvailability = {
						...(availability || {}),
						dailyLimit: 0,
						monthlyLimit: 0,
						exceptions: [],
						product: productId,
						type,
					};
					await updateAvailability(data);
				}
			} else {
				// For 'reservation' type: normal save with daily/monthly limits
				let data: InAppAvailability = {
					...(availability || {}),
					dailyLimit: availabilityDailyLimit,
					monthlyLimit: availabilityMonthlyLimit,
					exceptions: availabilityExceptions,
					product: productId,
					type,
				};
				if (accessType) {
					data.accessType = accessType;
				}
				await updateAvailability(data);
			}
			refetchAvailability();
		}
	}, [
		availability,
		availabilityDailyLimit,
		availabilityExceptions,
		availabilityMonthlyLimit,
		productId,
		refetchAvailability,
		type,
		updateAvailability,
		deleteAvailability,
		accessType,
		orderTypeEnabled,
	]);

	const addNewException = () => {
		if (newException) {
			const newExceptionDate = moment(newException.date);
			setAvailabilityExceptions((curr) => {
				const found = curr.findIndex((ex) => moment(ex.date).isSame(newExceptionDate, 'day'));
				if (found >= 0) {
					let newData = [...curr];
					newData[found] = newException;
					return newData;
				}
				return [...curr, newException];
			});
		}
		setNewException(null);
	};

	const removeException = (index: number) => {
		setAvailabilityExceptions((curr) => {
			return curr.filter((_, i) => i !== index);
		});
	};

	useEffect(() => {
		if (availability) {
			setAvailabilityDailyLimit(availability.dailyLimit);
			setAvailabilityExceptions(availability.exceptions || []);
			setAvailabilityMonthlyLimit(
				!isNaN(availability.monthlyLimit) && availability.monthlyLimit >= availability.dailyLimit
					? availability.monthlyLimit
					: availability.dailyLimit * 31
			);
		}
	}, [availability]);

	const exceptionsLimits = availabilityExceptions.map((ex) => ex.limit);

	// Determine if save button should be enabled
	const availabilitySaveEnabled =
		(reservationType !== ReservationType.parking || !!accessType) &&
		(type === InAppSaleType.order
			? // For 'order' type: enable save if state changed (availability exists !== switch enabled)
			  (!!availability !== orderTypeEnabled || availability?.type !== type)
			: // For 'reservation' type: enable save if any field changed
			  !availability ||
			  availability.dailyLimit !== availabilityDailyLimit ||
			  availability.monthlyLimit !== availabilityMonthlyLimit ||
			  availability.accessType !== accessType ||
			  availability.type !== type ||
			  (availability.exceptions?.length ?? 0) !== availabilityExceptions.length ||
			  !!availability?.exceptions?.find((ex) => !exceptionsLimits.includes(ex.limit)));

	return (
		<>
			llv
			<Typography variant="h6" sx={{ mt: 4, color: !productId ? 'grey' : undefined }}>
				Disponibilidad
			</Typography>
			{!productId && (
				<Typography variant="caption" sx={{ color: 'grey' }}>
					El modulo de disponibilidad sólo puede ser utilizado con productos existentes.
				</Typography>
			)}
			{loadingAvailabilities && <LinearProgress />}
			{productId && !loadingAvailabilities && (
				<FormControl fullWidth sx={{ mt: 2 }}>
					<InputLabel>Tipo</InputLabel>
					<Select
						labelId="type"
						id="type"
						label="Tipo"
						value={type}
						onChange={(ev) => setType(ev.target.value as InAppSaleType)}
					>
						{inAppSaleTypes.map((t) => {
							return (
								<MenuItem key={t} value={t}>
									{getType(t)}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>
			)}
			{type && type === InAppSaleType.reservation && (
				<FormControl fullWidth sx={{ mt: 2 }}>
					<InputLabel>Tipo de reserva</InputLabel>
					<Select
						labelId="reservationType"
						id="reservationType"
						label="Tipo de reserva"
						value={reservationType}
						onChange={(ev) => setReservationType(ev.target.value as ReservationType)}
					>
						{reservationTypes.map((t) => {
							return (
								<MenuItem key={t} value={t}>
									{getReservationType(t)}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>
			)}
			{productId && !loadingAvailabilities && reservationType === ReservationType.parking && vehicles.length && (
				<FormControl fullWidth sx={{ mt: 2 }}>
					<InputLabel>Tipo de acceso</InputLabel>
					<Select
						labelId="accessType"
						id="accessType"
						label="Tipo de acceso"
						value={accessType}
						onChange={(ev) => setAccessType(ev.target.value)}
					>
						{vehicles.map((v) => {
							return (
								<MenuItem key={`${v._id}`} value={`${v._id}`}>
									{v.description}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>
			)}
			{/* For 'order' type: show enable/disable switch */}
			{productId && !loadingAvailabilities && type === InAppSaleType.order && (
				<Box sx={{ mt: 2, mb: 2 }}>
					<FormControlLabel
						control={
							<Switch
								checked={orderTypeEnabled}
								onChange={(e) => setOrderTypeEnabled(e.target.checked)}
								disabled={loadingAvailability}
							/>
						}
						label={orderTypeEnabled ? 'Producto disponible para compra' : 'Producto no disponible'}
					/>
					<Typography variant="caption" display="block" sx={{ ml: 4, color: 'text.secondary' }}>
						{orderTypeEnabled
							? 'El producto estará visible y disponible para compra en la app'
							: 'El producto no aparecerá como disponible para compra'}
					</Typography>
				</Box>
			)}

			{/* For 'reservation' type: show daily/monthly limits */}
			{productId && !loadingAvailabilities && type === InAppSaleType.reservation && (
				<TextField
					id="availability-daily"
					type="number"
					label={'Por día'}
					fullWidth={true}
					value={availabilityDailyLimit}
					variant="outlined"
					sx={{ mt: 2, mb: 2 }}
					onChange={(newValue) => {
						const value = Number(newValue.target.value);
						setAvailabilityDailyLimit(value > 0 ? value : 0);
						setAvailabilityMonthlyLimit(value * 31);
					}}
					disabled={loadingAvailability}
				/>
			)}
			{productId && !loadingAvailabilities && type === InAppSaleType.reservation && (
				<TextField
					id="availability-total"
					type="number"
					label={'Mensual'}
					fullWidth={true}
					value={availabilityMonthlyLimit}
					variant="outlined"
					sx={{ mt: 2, mb: 2 }}
					onChange={(newValue) => {
						const value = Number(newValue.target.value);
						return setAvailabilityMonthlyLimit(
							value > 0 && value > availabilityDailyLimit ? value : availabilityDailyLimit
						);
					}}
					disabled={loadingAvailability}
				/>
			)}
			{productId && !loadingAvailabilities && (
				<Typography
					variant="h6"
					sx={{
						mt: 2,
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					Excepciones
					<Button
						disabled={availabilityDailyLimit <= 0}
						onClick={() => {
							setNewException((curr) => {
								return curr
									? null
									: {
											date: new Date(),
											limit: 0,
									  };
							});
						}}
					>
						{!newException && <Add />}
						{!!newException && <Close />}
					</Button>
				</Typography>
			)}
			{newException && !loadingAvailabilities && (
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
					<DatePicker
						value={moment(newException.date)}
						onChange={(newValue) => {
							setNewException((curr) => {
								if (newValue && curr) {
									return { ...curr, date: newValue.toDate() };
								}
								return curr;
							});
						}}
					/>
					<TextField
						id="exception-limit"
						type="number"
						label={'test'}
						fullWidth={true}
						value={newException.limit}
						variant="outlined"
						sx={{ mt: 2, mb: 2 }}
						onChange={(newValue) => {
							setNewException((curr) => {
								if (newValue && curr) {
									return { ...curr, limit: Number(newValue.target.value) };
								}
								return curr;
							});
						}}
						disabled={loadingAvailabilitiesActions}
					/>
					<Button disabled={!newException.limit || loadingAvailabilitiesActions} onClick={addNewException}>
						<Save color={!newException.limit || loadingAvailabilitiesActions ? 'disabled' : 'primary'} />
					</Button>
				</Box>
			)}
			{!loadingAvailabilities &&
				availabilityExceptions.map((availabilityException, index) => {
					return (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
							<DatePicker value={moment(availabilityException.date)} disabled={true} />
							<TextField
								id={`exception-limit-${index}`}
								type="number"
								label={'Disponibilidad'}
								fullWidth={true}
								value={availabilityException.limit}
								variant="outlined"
								sx={{ mt: 2, mb: 2 }}
								disabled={true}
							/>
							<Button onClick={() => removeException(index)}>
								<Delete color={'error'} />
							</Button>
						</Box>
					);
				})}
			{!loadingAvailabilities && (
				<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
					<Button
						variant="text"
						color="primary"
						size="small"
						sx={{ m: 1, mt: 4 }}
						onClick={saveAvailability}
						disabled={!availabilitySaveEnabled}
					>
						Guardar disponibilidad
					</Button>
				</div>
			)}
		</>
	);
}

function getReservationType(type: ReservationType): string {
	switch (type) {
		case ReservationType.parking:
			return 'Parking';
		default:
			return 'Resort';
	}
}

function getType(type: InAppSaleType): string {
	switch (type) {
		case InAppSaleType.reservation:
			return 'Reserva';
		default:
			return 'Compra';
	}
}
